import React from 'react';
import { Descriptions, Divider, Space, Tag, Typography } from 'antd';
import { displayRetainIQDate, displayRetainIQDateTime } from './retainiqDates';

const { Text, Paragraph } = Typography;

export type RetainIQLogDetail = {
  id: string;
  created_at: string;
  business_name?: string;
  agent_name?: string;
  created_by_name?: string;
  ticket_number?: string | null;
  payload: Record<string, unknown>;
};

function str(v: unknown): string {
  if (v == null || v === '') return '—';
  return String(v);
}

function listTags(items: unknown): React.ReactNode {
  if (!Array.isArray(items) || items.length === 0) return '—';
  return (
    <Space size={[4, 4]} wrap>
      {items.map((item) => (
        <Tag key={String(item)}>{String(item)}</Tag>
      ))}
    </Space>
  );
}

export const RetainIQCallLogDetail: React.FC<{
  log: RetainIQLogDetail;
  ticketLink?: (ticketNumber: string) => React.ReactNode;
}> = ({ log, ticketLink }) => {
  const p = log.payload || {};

  return (
    <div>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Logged">{displayRetainIQDateTime(log.created_at)}</Descriptions.Item>
        <Descriptions.Item label="Business">{str(log.business_name)}</Descriptions.Item>
        <Descriptions.Item label="Agent">{str(log.agent_name)}</Descriptions.Item>
        {log.created_by_name && log.created_by_name !== log.agent_name && (
          <Descriptions.Item label="Created by">{str(log.created_by_name)}</Descriptions.Item>
        )}
        <Descriptions.Item label="Ticket">
          {log.ticket_number && ticketLink ? ticketLink(String(log.ticket_number)) : str(log.ticket_number)}
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" plain>
        Call information
      </Divider>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Date / time">
          {[displayRetainIQDate(p.call_date), str(p.call_time)].filter((v) => v && v !== '—').join(' ') || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Duration (min)">{str(p.call_duration)}</Descriptions.Item>
        <Descriptions.Item label="Call type">{str(p.call_type)}</Descriptions.Item>
        <Descriptions.Item label="Contact method">{str(p.contact_method)}</Descriptions.Item>
        <Descriptions.Item label="Contact made">{str(p.contact_made)}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" plain>
        Account details
      </Divider>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Merchant ID">{str(p.merchant_id)}</Descriptions.Item>
        <Descriptions.Item label="Contact">{str(p.contact_name)}</Descriptions.Item>
        <Descriptions.Item label="Phone">{str(p.contact_phone)}</Descriptions.Item>
        <Descriptions.Item label="Email">{str(p.contact_email)}</Descriptions.Item>
        <Descriptions.Item label="Services">{listTags(p.business_services)}</Descriptions.Item>
        <Descriptions.Item label="Account tenure">{str(p.account_tenure)}</Descriptions.Item>
        <Descriptions.Item label="Risk level">{str(p.risk_level)}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" plain>
        Discussion
      </Divider>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Topics">{listTags(p.topics)}</Descriptions.Item>
        {p.topics_other && String(p.topics_other).trim() ? (
          <Descriptions.Item label="Other topics">{str(p.topics_other)}</Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Sentiment">{str(p.sentiment)}</Descriptions.Item>
        <Descriptions.Item label="Pain points">
          <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{str(p.pain_points)}</Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label="Positive feedback">
          <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{str(p.positive_notes)}</Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label="Commitments">
          <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{str(p.commitments)}</Paragraph>
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" plain>
        Follow-up & outcome
      </Divider>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Follow-up required">{str(p.followup_flag)}</Descriptions.Item>
        <Descriptions.Item label="Follow-up date">{displayRetainIQDate(p.followup_date)}</Descriptions.Item>
        <Descriptions.Item label="Follow-up action">{str(p.followup_action)}</Descriptions.Item>
        {p.followup_status ? (
          <Descriptions.Item label="Follow-up status">{str(p.followup_status)}</Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Call outcome">{str(p.call_outcome)}</Descriptions.Item>
        <Descriptions.Item label="NPS">{p.nps_score != null ? `${p.nps_score} / 10` : '—'}</Descriptions.Item>
        <Descriptions.Item label="Notes">
          <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{str(p.call_notes)}</Paragraph>
        </Descriptions.Item>
      </Descriptions>

      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 11 }}>
        Log ID: {log.id}
      </Text>
    </div>
  );
};
